import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import { GoogleSignInButton } from '../components/auth/GoogleSignInButton';
import { ForgotPasswordModal } from '../components/auth/ForgotPasswordModal';
import { AdminLoginModal } from '../components/auth/AdminLoginModal';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLogin } from '../hooks/auth/useLogin';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const { isLoading, handleLogin } = useLogin();
  const location = useLocation();
  const navigate = useNavigate();
  const from = location.state?.from || '/prestige-club';
  const promoCode = location.state?.promoCode;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await handleLogin(email, password);
    if (!success) {
      setPassword(''); // Clear password on error
    } else {
      // If there's a promo code and destination is subscription, add promo code to URL
      if (from === '/subscription' && promoCode) {
        navigate(`/subscription?promo=${promoCode}`, { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#2a1114] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="text-center text-3xl font-extrabold text-white">
          Welcome back
        </h2>
        <div className="mt-2 text-center">
          <button
            onClick={() => setIsAdminModalOpen(true)}
            className="text-sm text-white hover:opacity-80"
          >
            Admin Login
          </button>
        </div>
        <p className="mt-2 text-center text-sm text-white">
          Or{' '}
          <Link to="/create-account" className="text-[#ffffff] hover:opacity-80">
            Join Tezhly:Career Labs
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <GoogleSignInButton />
          
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-black"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-black">Or sign in with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-black">
                Email
              </label>
              <div className="mt-1 relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-black" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white border border-black rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-[#d8f477] focus:border-[#d8f477]"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-black">
                Password
              </label>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-black" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white border border-black rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-[#d8f477] focus:border-[#d8f477]"
                  required
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setIsForgotPasswordOpen(true)}
                className="text-sm text-black hover:opacity-80"
              >
                Forgot your password?
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-[#2a1114] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#ffffff] disabled:opacity-50"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>

      <ForgotPasswordModal 
        isOpen={isForgotPasswordOpen}
        onClose={() => setIsForgotPasswordOpen(false)}
      />
      
      <AdminLoginModal
        isOpen={isAdminModalOpen}
        onClose={() => setIsAdminModalOpen(false)}
        onSuccess={() => {
          setIsAdminModalOpen(false);
          window.location.href = '/admin';
        }}
      />
    </div>
  );
}